/**
 * Monta UMA vez o popup de indisponibilidade do servidor no nivel do
 * layout de autenticacao.
 *
 * Por que aqui e nao dentro do SignInForm: o `AuthLayout` renderiza o
 * formulario duas vezes (variantes desktop e mobile, uma escondida por
 * CSS). Como o Modal usa portal, um modal montado dentro do form apareceria
 * duplicado (os dois portais escapam do `hidden`). Este guard fica fora do
 * `{children}` duplicado, entao roda o health-check uma unica vez e mostra
 * um unico modal.
 *
 * Dois gatilhos:
 *  - proativo: `useBackendHealth` acusa a API fora do ar no carregamento;
 *  - sob demanda: o evento `optimus:server-unavailable`, disparado pelo form
 *    quando o login falha por rede/5xx (o email vai no detail para o
 *    fallback de WhatsApp) e pelo interceptor da `api` (`source: 'api'`, ou
 *    `'timeout'`, que só abre se o ping de saúde também falhar).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { SystemUnavailableModal } from './SystemUnavailableModal'
import { SERVER_UNAVAILABLE_EVENT } from '../../lib/apiError'
import { isBackendUp, useBackendHealth } from '../../hooks/useBackendHealth'
import { reportBackendDown } from '../../utils/backendDownAlert'
import type { ServerPhase } from '../../services/awsSchedulerService'

export { SERVER_UNAVAILABLE_EVENT }

const REABRIR_DEPOIS_MS = 60_000

export interface ServerUnavailableDetail {
  email?: string
  source?: string
}

export function ServerWakeGuard() {
  const [isOpen, setIsOpen] = useState(false)
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined)
  const health = useBackendHealth()
  const fechadoEm = useRef(0)
  const origem = useRef('signin')
  const fechar = useCallback(() => {
    fechadoEm.current = Date.now()
    setIsOpen(false)
  }, [])

  // Gatilho proativo: nao deixa digitar credencial num form morto.
  useEffect(() => {
    if (health === 'down') setIsOpen(true)
  }, [health])

  // Gatilho sob demanda: falha de login por rede/5xx.
  useEffect(() => {
    const onUnavailable = (event: Event) => {
      const detail = (event as CustomEvent<ServerUnavailableDetail>).detail
      // Dentro do app, cada requisição que falha dispara o evento: depois que
      // a pessoa fecha o popup, ele não volta antes de um minuto.
      const deDentroDoApp =
        detail?.source === 'api' || detail?.source === 'timeout'
      if (deDentroDoApp && Date.now() - fechadoEm.current < REABRIR_DEPOIS_MS) {
        return
      }
      origem.current = detail?.source ?? 'signin'
      const abrir = () => {
        setUserEmail(detail?.email)
        setIsOpen(true)
      }
      if (detail?.source !== 'timeout') {
        abrir()
        return
      }
      void isBackendUp().then(up => {
        // O ping leva até 5 s: a pessoa pode ter fechado o popup nesse meio tempo.
        if (!up && Date.now() - fechadoEm.current >= REABRIR_DEPOIS_MS) abrir()
      })
    }
    window.addEventListener(SERVER_UNAVAILABLE_EVENT, onUnavailable)
    return () =>
      window.removeEventListener(SERVER_UNAVAILABLE_EVENT, onUnavailable)
  }, [])

  // So alerta o Sentry quando o servidor esta ligado mas inacessivel
  // (incidente real), nunca no desligamento programado.
  const handleDiagnosis = useCallback((phase: ServerPhase) => {
    if (phase === 'running') void reportBackendDown({ source: origem.current })
  }, [])

  return (
    <SystemUnavailableModal
      isOpen={isOpen}
      onClose={fechar}
      onRetry={fechar}
      onDiagnosis={handleDiagnosis}
      userEmail={userEmail}
    />
  )
}
